'use client';
import React, { useMemo, useRef, useState } from 'react';
import Checkbox from '../Checkbox/Checkbox';
import Range from './components/Range';
import useFacetsSort, { useFacetPrice } from '@utils/hooks/sort/useFacets';
import { Maybe } from 'graphql/jsutils/Maybe';
import useDistinctFacetSelected from '@utils/hooks/sort/useDistinctFacetSelected';
import {
  DistinctFacetFragment,
  DistinctItem,
  RangeFacetFragment,
} from '@business/gql/graphql';
import { useMounted } from '@utils/hooks/useMounted';
import Input from '../inputs/Input';
import { Dictionary } from '@ts/dictionary';
import Paragraph from '../Paragraph/Paragraph';

function isInt(n: any) {
  return Number(n) === n && n % 1 === 0;
}

function isFloat(n: any) {
  return Number(n) === n && n % 1 !== 0;
}

type FacetRangeProps = {
  facet: RangeFacetFragment | undefined;
  minLabel: string;
  maxLabel: string;
};

export const FacetRange = (props: FacetRangeProps) => {
  const { facet, minLabel, maxLabel } = props;

  const setFacetPrice = useFacetPrice();

  if (
    !(
      isInt(facet?.min) ||
      isFloat(facet?.min) ||
      isInt(facet?.max) ||
      isFloat(facet?.max)
    )
  )
    return null;

  return (
    <Range
      minLabel={minLabel}
      maxLabel={maxLabel}
      min={facet?.min as number}
      max={facet?.max as number}
      values={[
        (facet?.selectedMin as number) ?? facet?.min,
        (facet?.selectedMax as number) ?? facet?.max,
      ]}
      attribute="price"
      onChange={(e) => {
        if (
          // @ts-ignore
          e.target.min === facet?.min &&
          // @ts-ignore
          e.target.max === facet?.max
        ) {
          setFacetPrice({ type: 'range', min: null, max: null });
        } else {
          // @ts-ignore
          setFacetPrice(e.target);
        }
      }}
    />
  );
};

type DistinctFacetsProps = {
  item: Maybe<DistinctItem> | undefined;
  dictionary: Dictionary;
  id: string | undefined;
};

function DistinctFacet(props: DistinctFacetsProps) {
  const setFacet = useFacetsSort();

  const selected = useDistinctFacetSelected({
    id: props.id ?? '',
    name: props.item?.item ?? '',
  });

  const mounted = useMounted();
  const { id = '', item } = props;
  if (!item) return null;
  if (!mounted)
    // To avoid hydration mismatch when reloading a page with filters in url
    return (
      <div className="flex place-self-start">
        <Checkbox
          name={`${props.dictionary[item.name] || item.name} (${item.count})`}
          value={item.item}
          checked={false}
          onChange={() => {
            setFacet({ id: id ?? '', name: item.item ?? '' });
          }}
        />
      </div>
    );
  return (
    <div className="flex place-self-start">
      <Checkbox
        name={`${props.dictionary[item.name] || item.name} (${item.count})`}
        value={item.item}
        checked={selected}
        onChange={() => {
          setFacet({
            id: id ?? '',
            name: item?.item ?? '',
          });
        }}
      />
    </div>
  );
}

type Props = {
  facet: DistinctFacetFragment;
  dictionary: Dictionary;
};

const SEARCHABLE_THRESHOLD = 20;

export function DistinctFacets({ facet, dictionary }: Props) {
  const wrapper = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>('');

  const searchable = useMemo(() => {
    if (!facet.items) return false;

    return (
      facet.name !== 'Category' && facet.items.length >= SEARCHABLE_THRESHOLD
    );
  }, [facet]);

  const filteredFacets = useMemo(
    () =>
      facet?.items?.filter((item: DistinctItem | null) => {
        if (!item) return false;
        const { count, name } = item;
        const included = name.toLowerCase().includes(search.toLowerCase());
        return count && count > 0 && included;
      }),
    [facet?.items, search],
  );

  return (
    <div>
      {searchable ? (
        <Input
          type="text"
          fullWidth
          placeholder={dictionary.filterSearchPlaceholder}
          onChange={({ target }) => setSearch(target.value)}
          classNames={{
            wrapper: 'mb-3 md:mb-4 md:mt-1',
            input:
              'focus-visible:-outline-offset-2 h-8 placeholder:text-sm lg:text-sm py-0 pl-2 placeholder:text-grey-600 border-gray-600',
          }}
        />
      ) : null}
      <div ref={wrapper}>
        <div className="relative w-full">
          {searchable && filteredFacets?.length === 0 ? (
            <Paragraph className="text-grey-200 text-xs">
              {dictionary.filterSearchNoResults}
            </Paragraph>
          ) : null}

          {filteredFacets?.map(
            (v) =>
              (v?.count ?? 0) > 0 && (
                <DistinctFacet
                  dictionary={dictionary}
                  key={`${facet?.name}-${v?.item}`}
                  item={v}
                  id={facet?.name}
                />
              ),
          )}
        </div>
      </div>
    </div>
  );
}
